import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ContentSectionService } from '../content-section.service';
import { ContentSectionQuery } from '../content-section.query';
import { MatDialog } from '@angular/material/dialog';
import { ContentSectionDialogComponent } from '../content-section-dialog/content-section-dialog.component';
import { ContentSectionAddDto } from '../../../../../../../api/src/content-section/dtos/content-section-add.dto';
import { ContentSectionUsageContext, PublicContentSection } from '../../../../../../../api/src/content-section/content-section.entity';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ContentSectionUpdateDto } from '../../../../../../../api/src/content-section/dtos/content-section-update.dto';
import { ContentSectionOpenEvent } from '../content-section.model';
import { PublicCreativeUnitPackageListing } from '../../../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity';
import { SessionQuery } from '../../session';
import { SelectedPartialListingEvent } from '../../../../pages/project/project.page';

@Component({
	selector: 'app-content-section-container',
	templateUrl: './content-section-container.component.html',
	styleUrls: ['./content-section-container.component.scss']
})
export class ContentSectionContainerComponent implements OnInit, OnChanges {
	@Input() usageContext: ContentSectionUsageContext;
	@Input() contextState: any;
	@Input() enableNewCreativePackageRequests: boolean;

	@Output() customEvent: EventEmitter<{ name: string; args: any[] }> = new EventEmitter();
	@Output() updateOrder: EventEmitter<{ contentSection: PublicContentSection; newOrder: number }> = new EventEmitter();

	@Output() selectedListing: EventEmitter<PublicCreativeUnitPackageListing> = new EventEmitter<PublicCreativeUnitPackageListing>();
	@Output() selectedPartialListing: EventEmitter<SelectedPartialListingEvent> = new EventEmitter<SelectedPartialListingEvent>();

	public contentSections$ = this.contentSectionQuery.selectAll();

	public adminMode: boolean = false;
	public isAtLeastAdmin;
	private loadedContext: string | null = null;

	constructor(
		public readonly contentSectionQuery: ContentSectionQuery,
		public readonly contentSectionService: ContentSectionService,
		public readonly sessionQuery: SessionQuery,
		public readonly dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.isAtLeastAdmin = this.sessionQuery.isAtLeastAdmin();
		
		// Load data on initialization if we have a context
		if (this.usageContext) {
			this.loadedContext = this.usageContext;
			this.loadData();
		}
	}

	ngOnChanges(changes: any): void {
		this.isAtLeastAdmin = this.sessionQuery.isAtLeastAdmin();

		// Only reload if the usageContext actually changed to a different value
		if (changes.usageContext && 
		    changes.usageContext.currentValue && 
		    changes.usageContext.currentValue !== changes.usageContext.previousValue) {
			this.loadedContext = changes.usageContext.currentValue;
			this.loadData();
		}
	}

	loadData() {
		this.contentSectionService.find(this.usageContext).subscribe();
	}

	async openDialog(ev: ContentSectionOpenEvent) {
		let dialogRef = await this.dialog.open(ContentSectionDialogComponent, {
			data: {
				contentSection: ev.contentSection || {},
				contextState: this.contextState
			},
			panelClass: ['fullscreen', 'stretch'],
			disableClose: true
		});

		const result = await dialogRef.afterClosed().toPromise();

		console.log(result, ev.order, ev.contentSection);

		if (result) {
			result.usageContext = this.usageContext;

			if (ev.order) {
				result.order = ev.order.toString();
			}

			if (ev.contentSection) {
				this.contentSectionService.update(ev.contentSection.id, result as ContentSectionUpdateDto).subscribe(() => {
					this.loadData();
				});
			} else {
				this.contentSectionService.create(result as ContentSectionAddDto).subscribe(() => {
					this.loadData();
				});
			}
		}
	}

	public async delete(contentSection: PublicContentSection) {
		let dialogRef = await this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: $localize`:Delete Confirm Dialog@@contentSectionDeleteConfirmationDialogTitle:Delete Content Section`,
				message: $localize`:Delete Confirm Dialog@@contentSectionDeleteConfirmationDialogMessage:Are you sure you want to delete ${contentSection.name}?`,
				remove: true
			}
		});

		const result = await dialogRef.afterClosed().toPromise();

		if (!result) {
			return;
		}

		this.contentSectionService.delete(contentSection.id).subscribe(() => {
			this.loadData();
		});
	}

	handleUpdateOrder(event: { contentSection: PublicContentSection; newOrder: string }) {
		// Force newOrder to be a string.
		event.newOrder = event.newOrder.toString();

		this.contentSectionService.update(event.contentSection.id, { order: event.newOrder } as ContentSectionUpdateDto).subscribe(() => {
			this.loadData();
		});
	}
}
